import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { getAdaptiveWeightsSummary } from "../lib/api";
import { SYMBOL_ID_MAP } from "../lib/runtimeConfig";

const ENGINE_PRESETS = [
    { key: "dream", label: "Dream Nebula" },
    { key: "horizon", label: "Horizon Strategy" },
    { key: "scalp", label: "Scalping Strategy" },
];

const ADAPTIVE_SYMBOL_TO_ENGINE_ID = new Map(
    [
        ["AVAX", "AVALANCHE_2"],
        ["HBAR", "HEDERA_HASHGRAPH"],
    ].map(([symbol, engineId]) => [symbol.toUpperCase(), String(engineId)]),
);

const SLUG_TO_SYMBOL = new Map(
    Object.entries(SYMBOL_ID_MAP || {}).map(([symbol, slug]) => [String(slug).toLowerCase(), symbol.toUpperCase()]),
);

const ENGINE_ALIASES = new Map(
    [
        ["dream", "dream"],
        ["dreamnebula", "dream"],
        ["horizon", "horizon"],
        ["horizonengine", "horizon"],
        ["scalping", "scalp"],
        ["scalpingengine", "scalp"],
        ["scalp", "scalp"],
    ].map(([raw, normalized]) => [normalizeKey(raw), normalized]),
);

function normalizeKey(value = "") {
    return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function canonicalCoin(value = "") {
    const normalized = normalizeKey(value);
    const slugMatch = SLUG_TO_SYMBOL.get(normalized);
    if (slugMatch) return slugMatch;
    return value.toUpperCase();
}

function formatCoinLabel(slug = "") {
    if (!slug) return "Adaptive Weights";
    return slug
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
}

function formatWeightValue(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === "number" && Number.isFinite(value)) {
        const percent = Math.abs(value) <= 1 ? value * 100 : value;
        const decimals = percent < 10 ? 2 : 1;
        return `${percent.toFixed(decimals)}%`;
    }
    return String(value);
}

export default function WeightGraph({ coin }) {
    const [engines, setEngines] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [engineList, setEngineList] = useState(ENGINE_PRESETS.map((e) => e.key));
    const [sourceStatus, setSourceStatus] = useState('unknown');

    useEffect(() => {
        let active = true;
        if (!coin) return () => {};
        setLoading(true);
        setError("");

        async function loadWeights() {
            try {
                const summaryRes = await getAdaptiveWeightsSummary();

                if (!active) return;

                if (summaryRes?.unauth) {
                    setSourceStatus("unavailable");
                    setError("Authentication required for adaptive weights.");
                    setEngines({});
                    return;
                }

                if (!summaryRes?.ok || !summaryRes?.data) {
                    setSourceStatus(summaryRes?.status === "stale" ? "stale" : "unavailable");
                    setError("Adaptive weights unavailable.");
                    setEngines({});
                    return;
                }

                const summary = summaryRes.data || {};
                if (summary?.source_status) setSourceStatus(String(summary.source_status));
                else if (summaryRes.status === "stale") setSourceStatus("stale");
                else setSourceStatus(Array.isArray(summary?.coins) && summary.coins.length > 0 ? "ok" : "empty");
                const coinKey = canonicalCoin(coin);
                const slugKey = SYMBOL_ID_MAP?.[coinKey];
                const engineCoinId = ADAPTIVE_SYMBOL_TO_ENGINE_ID.get(coinKey);
                const summaryCoins = Array.isArray(summary?.coins) ? summary.coins : [];
                const lookupKeys = new Set(
                    [coinKey, coin, slugKey, engineCoinId].filter(Boolean).map((value) => normalizeKey(String(value))),
                );
                const summaryEntry = summaryCoins.find((entry) => lookupKeys.has(normalizeKey(entry?.coin)));

                const normalizedEngines = Object.entries(summaryEntry?.engines || {}).reduce((acc, [key, value]) => {
                    const mappedKey = ENGINE_ALIASES.get(normalizeKey(key)) || normalizeKey(key);
                    acc[mappedKey] = value;
                    return acc;
                }, {});

                const availableEngines = ENGINE_PRESETS.map((e) => e.key);
                if (active) setEngineList(availableEngines);

                const nextEngines = {};
                availableEngines.forEach((engineKey) => {
                    const summaryEngine = normalizedEngines?.[engineKey] || normalizedEngines?.[normalizeKey(engineKey)];
                    const weight = summaryEngine?.weight ?? null;
                    const status = summaryEngine?.status || (summaryEntry ? "no_data" : "no_data_yet");
                    nextEngines[engineKey] = { status, weight };
                });

                setEngines(nextEngines);

                if (!summaryEntry && summaryRes.status === "no_data_yet") {
                    setError("");
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Unable to load adaptive weights", err);
                if (active) {
                    setSourceStatus("malformed");
                    setEngines({});
                    setError("Unable to load adaptive weights.");
                }
            } finally {
                if (active) setLoading(false);
            }
        }

        loadWeights();

        return () => {
            active = false;
        };
    }, [coin]);

    const rows = useMemo(() => {
        const availableEngines = engineList.length > 0 ? engineList : ENGINE_PRESETS.map((p) => p.key);
        return availableEngines.map((key) => {
            const preset = ENGINE_PRESETS.find((p) => normalizeKey(p.key) === normalizeKey(key));
            const entry = engines?.[key] || engines?.[normalizeKey(key)];
            return { key, label: preset?.label || formatCoinLabel(key), status: entry?.status, weight: entry?.weight };
        });
    }, [engineList, engines]);

    const hasWeights = useMemo(
        () => rows.some((row) => row?.status === "calibrated" && row.weight !== undefined && row.weight !== null),
        [rows],
    );

    return (
        <div
            className="bg-gray-900/80 border border-gray-800/80 rounded-2xl p-4 flex flex-col gap-4"
            data-testid="adaptive-weight-card"
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-[0.65rem] uppercase tracking-wide text-indigo-300/70">Adaptive Weights</p>
                    <h3 className="text-lg font-semibold text-white leading-tight">
                        {formatCoinLabel(coin)} Adaptive Weights
                    </h3>
                    <p className="text-xs text-gray-400 mt-1">
                        Per-engine view for {coin?.toUpperCase()} across Nebula strategies.
                    </p>
                    <p className="text-[0.7rem] text-amber-200/80 mt-2 max-w-xl" data-testid="adaptive-weight-evidence-caveat">
                        Adaptive weights are telemetry/config visualization only: not clean strategy-quality
                        validation, not a tuning-ready signal, not profitability evidence, and not
                        live-readiness evidence. Clean samples remain insufficient unless clean readiness reports
                        otherwise.
                    </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                    <span className="text-[0.65rem] uppercase tracking-wide text-gray-500">Explorer</span>
                    <span className="rounded border border-gray-700 px-2 py-0.5 text-[0.65rem] uppercase tracking-wide text-gray-400" data-testid="adaptive-weight-source-status">source: {sourceStatus || "unknown"}</span>
                </div>
            </div>
            {error && (
                <div className="text-xs text-red-400 bg-red-900/20 border border-red-900/40 rounded-md px-3 py-2">
                    {error}
                </div>
            )}
            <div className="rounded-xl border border-gray-800/70 bg-gray-800/50 divide-y divide-gray-800/70">
                {rows.map((row) => {
                    const formattedWeight =
                        row.weight !== undefined && row.weight !== null ? formatWeightValue(row.weight) : null;
                    const notTraded = row.status === "not_traded";
                    let displayValue = "No data yet";
                    if (notTraded) displayValue = "Not traded";
                    else if (formattedWeight) displayValue = formattedWeight;
                    else if (!loading && !hasWeights)
                        displayValue = ["no_data", "no_data_yet"].includes(row.status)
                            ? "No data yet"
                            : "Not calibrated";
                    return (
                        <div
                            key={row.key}
                            className="flex items-center justify-between px-3 py-2 text-sm"
                        >
                            <span className="font-medium text-gray-100">{row.label}</span>
                            <span className={`text-gray-400${notTraded ? " italic" : ""}`}>
                                {displayValue}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

WeightGraph.propTypes = {
    coin: PropTypes.string.isRequired,
};
